import React, { useEffect, useRef, useState } from "react";
import { Search, FileText, Send, Building2, Upload, Award, LayoutGrid, List, ShieldCheck, BadgeDollarSign, Clock } from "lucide-react";
import api from "../lib/api";
import { useToast } from "../hooks/use-toast";
import KanbanBoard from "../components/KanbanBoard";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const STATUS_COLORS = {
  Draft: "bg-slate-100 text-slate-600", Submitted: "bg-blue-100 text-blue-700",
  "Under Review": "bg-amber-100 text-amber-700", Offer: "bg-green-100 text-green-700",
  Rejected: "bg-red-100 text-red-700", Accepted: "bg-emerald-100 text-emerald-700",
  Declined: "bg-orange-100 text-orange-700", Withdrawn: "bg-slate-100 text-slate-500",
};
const STATUS_TINT = {
  Draft: "#64748b", Submitted: "#2563eb", "Under Review": "#d97706", Offer: "#16a34a",
  Rejected: "#dc2626", Accepted: "#059669", Declined: "#ea580c", Withdrawn: "#94a3b8",
};

export default function Applications() {
  const { toast } = useToast();
  const [apps, setApps] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [filter, setFilter] = useState("All");
  const [view, setView] = useState("table");
  const [uniFilter, setUniFilter] = useState("All");
  const [intakeFilter, setIntakeFilter] = useState("All");
  const moveApp = async (id, status) => {
    setApps((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
    try {
      const { data } = await api.put(`/applications/${id}`, { status });
      if (data?.pending_approval) { toast({ title: "Submitted for approval", description: data.message || "Awaiting approval per the matrix." }); load(); }
      else toast({ title: `Moved to ${status}` });
    } catch { toast({ title: "Could not update", variant: "destructive" }); load(); }
  };
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const [approvals, setApprovals] = useState([]);
  const offerRef = useRef(null);

  const load = () => api.get("/applications", { params: filter !== "All" ? { status: filter } : {} }).then((r) => setApps(r.data)).catch(() => {});
  useEffect(() => { load(); }, [filter]);
  useEffect(() => { api.get("/applications/meta").then((r) => setStatuses(r.data.statuses)).catch(() => {}); }, []);

  const loadApprovals = (id) => api.get(`/approvals/for/application/${id}`).then((r) => setApprovals(r.data)).catch(() => setApprovals([]));
  const openApp = async (id) => { setActive(id); const { data } = await api.get(`/applications/${id}`); setDetail(data); loadApprovals(id); };
  const refresh = async () => { if (active) { const { data } = await api.get(`/applications/${active}`); setDetail(data); load(); loadApprovals(active); } };
  const setStatus = async (v) => {
    const { data } = await api.put(`/applications/${active}`, { status: v });
    if (data?.pending_approval) toast({ title: "Submitted for approval", description: data.message || "Awaiting approval per the matrix." });
    else toast({ title: `Status: ${v}` });
    refresh();
  };
  const addNote = async () => { if (!note.trim()) return; await api.post(`/applications/${active}/notes`, { text: note }); setNote(""); refresh(); };
  const feeAction = async (kind) => {
    try {
      const { data } = await api.post(`/applications/${active}/${kind}`, {});
      if (data?.pending_approval) toast({ title: "Submitted for approval", description: data.message || "Awaiting approval per the matrix." });
      else toast({ title: kind === "waive-fee" ? "Fee waived" : "Fee marked Paid" });
      refresh();
    } catch (e) { toast({ title: "Action failed", description: e?.response?.data?.detail || "", variant: "destructive" }); }
  };

  const onOfferLetter = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const kind = file.type.startsWith("image") ? "image" : "file";
      const { data } = await api.post(`/applications/${active}/offer-letter`, { name: file.name, data: reader.result, kind });
      if (data?.pending_approval) toast({ title: "Offer letter saved — pending approval", description: data.message || "Status change to Offer awaits approval per the matrix." });
      else toast({ title: "Offer letter uploaded", description: "Student notified & moved to Offer." });
      refresh();
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const filtered = apps.filter((a) => JSON.stringify(a).toLowerCase().includes(q.toLowerCase()))
    .filter((a) => uniFilter === "All" || (a.university_name || "") === uniFilter)
    .filter((a) => intakeFilter === "All" || (a.intake || "") === intakeFilter);
  const uniOptions = [...new Set(apps.map((a) => a.university_name).filter(Boolean))];
  const intakeOptions = [...new Set(apps.map((a) => a.intake).filter(Boolean))];

  return (
    <div>
      <h1 className="font-display text-2xl md:text-3xl font-bold text-[#012a1e]">University Applications</h1>
      <p className="text-slate-500 mt-1 text-sm">Review and progress student applications to partner universities.</p>

      <div className="flex flex-col md:flex-row md:items-center gap-3 mt-5">
        <div className="relative flex-1 max-w-sm">
          <Search size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search applications..." className="pl-10 h-11 rounded-full" />
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {["All", ...statuses].map((s) => (
            <button key={s} onClick={() => setFilter(s)} className={`px-3.5 py-1.5 rounded-full text-sm font-medium ${filter === s ? "bg-[#004030] text-white" : "bg-[#f0f6f3] text-[#004030]"}`}>{s}</button>
          ))}
          <div className="flex rounded-full border border-slate-200 overflow-hidden ml-1" data-testid="apps-view-toggle">
            <button onClick={() => setView("table")} title="Table view" className={`px-3 py-1.5 ${view === "table" ? "bg-[#004030] text-white" : "bg-white text-[#004030]"}`} data-testid="apps-view-table"><List size={15} /></button>
            <button onClick={() => setView("kanban")} title="Kanban view" className={`px-3 py-1.5 ${view === "kanban" ? "bg-[#004030] text-white" : "bg-white text-[#004030]"}`} data-testid="apps-view-kanban"><LayoutGrid size={15} /></button>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="apps-kanban-filters">
        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Filter</span>
        <Select value={uniFilter} onValueChange={setUniFilter}>
          <SelectTrigger className="h-9 w-52 rounded-full bg-white text-sm" data-testid="apps-filter-university"><SelectValue placeholder="University" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="All">All universities</SelectItem>
            {uniOptions.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={intakeFilter} onValueChange={setIntakeFilter}>
          <SelectTrigger className="h-9 w-40 rounded-full bg-white text-sm" data-testid="apps-filter-intake"><SelectValue placeholder="Intake" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="All">All intakes</SelectItem>
            {intakeOptions.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
          </SelectContent>
        </Select>
        {(uniFilter !== "All" || intakeFilter !== "All") && (
          <button onClick={() => { setUniFilter("All"); setIntakeFilter("All"); }} data-testid="apps-filter-clear" className="text-xs font-semibold text-[#004030] hover:underline px-2">Clear</button>
        )}
      </div>

      {view === "kanban" ? (
        <div className="mt-5">
          <KanbanBoard
            columns={statuses}
            items={filtered}
            getId={(a) => a.id}
            getStatus={(a) => a.status}
            columnTint={STATUS_TINT}
            onMove={moveApp}
            onCardClick={(a) => openApp(a.id)}
            renderCard={(a) => (
              <div>
                <p className="font-semibold text-sm text-[#1a231f]">{a.student_name}</p>
                <p className="text-xs text-slate-500 truncate flex items-center gap-1"><Building2 size={11} /> {a.university_name}</p>
                <p className="text-xs text-slate-400 truncate mt-0.5">{a.programme || "—"} · {a.intake || "—"}</p>
                <p className="text-[10px] text-slate-400 mt-2 flex items-center gap-1"><FileText size={11} /> {a.documents_count} doc(s)</p>
              </div>
            )}
          />
        </div>
      ) : (
      <div className="bg-white rounded-2xl border border-slate-100 mt-5 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-[#f7faf8] text-left text-slate-500">
            <th className="px-4 py-3 font-semibold">Student</th><th className="px-4 py-3 font-semibold">University</th>
            <th className="px-4 py-3 font-semibold">Programme</th><th className="px-4 py-3 font-semibold">Intake</th>
            <th className="px-4 py-3 font-semibold">Status</th><th className="px-4 py-3 font-semibold">Docs</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((a) => (
              <tr key={a.id} onClick={() => openApp(a.id)} className="hover:bg-[#f7faf8] cursor-pointer">
                <td className="px-4 py-3"><p className="font-medium text-[#012a1e]">{a.student_name}</p><p className="text-xs text-slate-400">{a.student_email}</p></td>
                <td className="px-4 py-3 text-slate-600">{a.university_name}</td>
                <td className="px-4 py-3 text-slate-600">{a.programme || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{a.intake || "—"}</td>
                <td className="px-4 py-3"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_COLORS[a.status]}`}>{a.status}</span></td>
                <td className="px-4 py-3 text-slate-500 text-xs"><span className="inline-flex items-center gap-1"><FileText size={13} />{a.documents_count}</span></td>
              </tr>
            ))}
            {!filtered.length && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No applications yet.</td></tr>}
          </tbody>
        </table>
      </div>
      )}

      <Sheet open={!!active} onOpenChange={(o) => { if (!o) { setActive(null); setDetail(null); } }}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {detail && (
            <>
              <SheetHeader><SheetTitle className="font-display text-xl">{detail.student_name}</SheetTitle></SheetHeader>
              <p className="text-slate-500 text-sm mt-1">{detail.student_email}</p>
              <div className="mt-4 bg-[#f7faf8] rounded-xl p-4 text-sm space-y-1">
                <p className="flex items-center gap-2 font-semibold text-[#012a1e]"><Building2 size={16} className="text-[#004030]" /> {detail.university_name}</p>
                <p className="text-slate-600">{detail.programme || "—"} · {detail.level || "—"} · {detail.intake || "—"}</p>
                {(detail.campus || detail.field_of_study) && <p className="text-slate-600">Campus: {detail.campus || "—"} · Field: {detail.field_of_study || "—"}</p>}
                <p className="text-slate-600">Nationality: {detail.nationality || "—"} · DOB: {detail.dob || "—"}</p>
                <p className="text-slate-600">Prev. education: {detail.prev_education || "—"}</p>
              </div>

              <div className="mt-4">
                <label className="text-xs text-slate-500">Update Status</label>
                <Select value={detail.status} onValueChange={setStatus}>
                  <SelectTrigger className="h-11 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div className="mt-5">
                <h3 className="font-semibold text-[#012a1e] text-sm">Documents</h3>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {(detail.documents || []).map((f) => (
                    <a key={f.id} href={f.data} target="_blank" rel="noopener noreferrer" className="border border-slate-200 rounded-lg p-2 text-center hover:border-[#004030]">
                      {f.kind === "image" ? <img src={f.data} alt={f.name} className="h-14 w-full object-cover rounded" /> : <FileText size={26} className="mx-auto text-[#004030]" />}
                      <p className="text-[10px] truncate mt-1">{f.name}</p>
                    </a>
                  ))}
                  {!(detail.documents || []).length && <p className="text-xs text-slate-400 col-span-3">No documents uploaded.</p>}
                </div>
              </div>

              <div className="mt-5">
                <h3 className="font-semibold text-[#012a1e] text-sm flex items-center gap-1.5"><Award size={15} className="text-[#c9a227]" /> Offer Letter</h3>
                <input ref={offerRef} type="file" hidden onChange={onOfferLetter} data-testid="offer-letter-input" />
                {detail.offer_letter ? (
                  <div className="mt-2 flex items-center gap-2 rounded-lg border border-slate-200 p-2.5">
                    <FileText size={18} className="text-[#004030]" />
                    <a href={detail.offer_letter.data} target="_blank" rel="noopener noreferrer" className="text-sm text-[#004030] flex-1 truncate hover:underline">{detail.offer_letter.name}</a>
                    <Button onClick={() => offerRef.current?.click()} variant="outline" size="sm" className="rounded-full text-xs h-8" data-testid="replace-offer-letter">Replace</Button>
                  </div>
                ) : (
                  <Button onClick={() => offerRef.current?.click()} variant="outline" className="mt-2 w-full rounded-xl border-dashed" data-testid="upload-offer-letter">
                    <Upload size={15} className="mr-1.5" /> Upload offer letter (moves to Offer + emails student)
                  </Button>
                )}
              </div>

              <div className="mt-5">
                <h3 className="font-semibold text-[#012a1e] text-sm flex items-center gap-1.5"><BadgeDollarSign size={15} className="text-[#004030]" /> Application Fee</h3>
                <p className="text-xs text-slate-500 mt-1">Status: <span className="font-semibold text-[#012a1e]">{detail.fee_status || "Unpaid"}</span>{detail.fee_waiver_note ? ` · ${detail.fee_waiver_note}` : ""}</p>
                {(() => { const feePending = approvals.some((a) => a.status === "Pending" && (a.action === "fee_waiver" || a.action === "mark_paid"));
                  if (detail.fee_status === "Paid" || detail.fee_status === "Waived") return null;
                  if (feePending) return <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5" data-testid="fee-pending-badge"><Clock size={13} /> Pending approval</div>;
                  return (
                  <div className="flex gap-2 mt-2">
                    <Button onClick={() => feeAction("waive-fee")} variant="outline" size="sm" className="rounded-full text-xs h-8 flex-1" data-testid="waive-fee-btn">Waive fee</Button>
                    <Button onClick={() => feeAction("mark-paid")} size="sm" className="rounded-full text-xs h-8 flex-1 bg-[#004030] text-white" data-testid="mark-paid-btn">Mark paid (manual)</Button>
                  </div>); })()}
              </div>

              {approvals.length > 0 && (
                <div className="mt-5" data-testid="approval-history">
                  <h3 className="font-semibold text-[#012a1e] text-sm flex items-center gap-1.5"><ShieldCheck size={15} className="text-[#004030]" /> Approval History</h3>
                  <div className="space-y-2 mt-2">
                    {approvals.map((a) => (
                      <div key={a.id} className="rounded-lg border border-slate-200 p-2.5" data-testid={`approval-item-${a.id}`}>
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-semibold text-[#012a1e]">{a.summary}</p>
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${a.status === "Approved" ? "bg-emerald-100 text-emerald-700" : a.status === "Rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{a.status}</span>
                        </div>
                        <p className="text-[10px] text-slate-400 mt-0.5">Requested by {a.requested_by_name} · {new Date(a.created_at).toLocaleString()}</p>
                        <div className="mt-1.5 space-y-1">
                          {(a.steps || []).map((s, i) => (
                            <div key={i} className="flex items-center gap-1.5 text-[11px]">
                              {s.status === "Approved" ? <ShieldCheck size={12} className="text-emerald-600" /> : s.status === "Rejected" ? <span className="text-red-600">✕</span> : <Clock size={12} className="text-amber-500" />}
                              <span className="text-slate-600">{s.label}</span>
                              {s.approved_by_name && <span className="text-slate-400">· {s.status} by {s.approved_by_name}{s.at ? ` (${new Date(s.at).toLocaleDateString()})` : ""}{s.note ? ` — "${s.note}"` : ""}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-5">
                <h3 className="font-semibold text-[#012a1e] text-sm">Notes to student</h3>
                <div className="space-y-2 mt-2 max-h-40 overflow-y-auto">
                  {(detail.notes || []).map((n) => (
                    <div key={n.id} className="bg-[#f7faf8] rounded-lg p-3"><p className="text-sm text-slate-700">{n.text}</p><p className="text-[10px] text-slate-400 mt-1">{n.by_name} · {new Date(n.at).toLocaleString()}</p></div>
                  ))}
                </div>
                <div className="flex gap-2 mt-3">
                  <Input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} placeholder="Add an update..." className="h-10" />
                  <Button onClick={addNote} className="bg-[#004030] text-white h-10 px-3"><Send size={16} /></Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
